import { useCallback } from 'react';
import {
  InfiniteData,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useQueryClient,
} from 'react-query';

import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

import {
  IAnyPagination,
  IChatPagination,
  IKeyPagination,
  IPagination,
} from '../model';

const _sumItems = (count: number, page: IAnyPagination<unknown>) =>
  count + page.items.length;

export type UsePaginationQueryResult<T> = UseInfiniteQueryResult<T, unknown> & {
  count: number;
  totalCount: number;
};

export const useChatPaginationQuery = <T extends IChatPagination<unknown>>(
  key: QueryKey,
  fetcher: QueryFunction<T>,
  options?: Partial<UseInfiniteQueryOptions<T, unknown, T, T, QueryKey>>
): UsePaginationQueryResult<T> => {
  const query = useInfiniteQuery(key, fetcher, {
    getNextPageParam: (lastPage) =>
      lastPage.has_next ? lastPage.page + 1 : undefined,
    staleTime: minutesToMilliseconds(15),
    cacheTime: minutesToMilliseconds(15),
    ...options,
  });

  const pages = query.data?.pages;
  return {
    ...query,
    count: pages?.reduce(_sumItems, 0) ?? 0,
    totalCount: pages?.[pages.length - 1].total_count ?? 0,
  };
};

export const usePaginationQuery = <T extends IPagination<unknown>>(
  key: QueryKey,
  fetcher: QueryFunction<T>,
  options?: Partial<UseInfiniteQueryOptions<T, unknown, T, T, QueryKey>>
): UsePaginationQueryResult<T> => {
  const query = useInfiniteQuery(key, fetcher, {
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.page + 1 : undefined,
    staleTime: minutesToMilliseconds(15),
    cacheTime: minutesToMilliseconds(15),
    ...options,
  });

  const pages = query.data?.pages;
  return {
    ...query,
    count: pages?.reduce(_sumItems, 0) ?? 0,
    totalCount: pages?.[pages.length - 1].totalCount ?? 0,
  };
};

export const useKeyPaginationQuery = <T extends IKeyPagination<unknown>>(
  key: QueryKey,
  fetcher: QueryFunction<T>,
  options?: Partial<UseInfiniteQueryOptions<T, T, T, T, QueryKey>>
): UsePaginationQueryResult<T> => {
  const query = useInfiniteQuery(key, fetcher, {
    getNextPageParam: (lastPage) => lastPage.lastEvaluatedKey ?? lastPage.next,
    staleTime: minutesToMilliseconds(30),
    cacheTime: minutesToMilliseconds(30),
    ...options,
  });
  return {
    ...query,
    count: query.data?.pages?.reduce(_sumItems, 0) ?? 0,
    totalCount: 0,
  };
};

type OptimisticIdentifier = number | string;

type OptimisticUpdateOptions = {
  id?: OptimisticIdentifier;
};

export const useOptimisticUpdate = () => {
  const queryClient = useQueryClient();

  const append = useCallback(
    <T extends { id: OptimisticIdentifier }>(
      key: QueryKey,
      newValue: T,
      { prepend = false }: { prepend?: boolean } = {}
    ) => {
      const prevData:
        | InfiniteData<IAnyPagination<T>>
        | IAnyPagination<T>
        | T[]
        | undefined = queryClient.getQueryData(key);

      if (!prevData) {
        return;
      }

      if ('pageParams' in prevData) {
        queryClient.setQueryData(key, {
          pageParams: prevData.pageParams,
          pages: prevData.pages.map((page, i) => {
            if (prepend && i === 0) {
              return {
                ...page,
                items: [newValue].concat(page.items),
              };
            } else if (!prepend && i === prevData.pages.length - 1) {
              return {
                ...page,
                items: page.items.concat(newValue),
              };
            }
          }),
        });
      } else if (Array.isArray(prevData)) {
        queryClient.setQueryData(
          key,
          prepend ? [newValue].concat(prevData) : prevData.concat(newValue)
        );
      } else if ('items' in prevData) {
        queryClient.setQueryData(key, {
          ...prevData,
          items: prepend
            ? [newValue].concat(prevData.items)
            : prevData.items.concat(newValue),
        });
      }
    },
    [queryClient]
  );

  const update = useCallback(
    <T extends { id: OptimisticIdentifier }>(
      key: QueryKey,
      newValue: Partial<T> | ((item: T) => T),
      { id }: OptimisticUpdateOptions = {}
    ) => {
      const prevData:
        | InfiniteData<IAnyPagination<T>>
        | IAnyPagination<T>
        | T[]
        | T
        | undefined = queryClient.getQueryData(key);

      if (!prevData) {
        return;
      }

      const handleItem = (item: T) =>
        typeof newValue === 'function'
          ? newValue(item)
          : { ...item, ...newValue };

      if ('pageParams' in prevData) {
        queryClient.setQueryData(key, {
          pageParams: prevData.pageParams,
          pages: prevData.pages.map((page) => ({
            ...page,
            items: page.items.map((item) => {
              if (!id) {
                return handleItem(item);
              }

              return item.id === id ? handleItem(item) : item;
            }),
          })),
        });
      } else if (Array.isArray(prevData)) {
        queryClient.setQueryData(
          key,
          prevData.map((item) => {
            if (!id) {
              return handleItem(item);
            }

            return item.id === id ? handleItem(item) : item;
          })
        );
      } else if ('items' in prevData) {
        queryClient.setQueryData(key, {
          ...prevData,
          items: prevData.items.map((item) => {
            if (!id) {
              return handleItem(item);
            }

            return item.id === id ? handleItem(item) : item;
          }),
        });
      } else {
        queryClient.setQueryData(key, handleItem(prevData));
      }
    },
    [queryClient]
  );

  const remove = useCallback(
    <T extends { id: number }>(key: QueryKey, id: number) => {
      const prevData:
        | InfiniteData<IAnyPagination<T>>
        | IAnyPagination<T>
        | T[]
        | T
        | undefined = queryClient.getQueryData(key);

      if (!prevData) {
        return;
      }

      if ('pageParams' in prevData) {
        queryClient.setQueryData(key, {
          pageParams: prevData.pageParams,
          pages: prevData.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item.id !== id),
          })),
        });
      } else if (Array.isArray(prevData)) {
        queryClient.setQueryData(
          key,
          prevData.filter((data) => data.id !== id)
        );
      } else if ('items' in prevData) {
        queryClient.setQueryData(key, {
          ...prevData,
          items: prevData.items.filter((item) => item.id !== id),
        });
      }
    },
    [queryClient]
  );

  return { append, update, remove };
};

export const getRouterQuery = (value: string | string[] | undefined) => {
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return parseInt(value as string);
  }

  return value;
};

export const changeQueryWithoutRendering = (newQuery: {
  [key: string]: string;
}) => {
  const queryString = new URLSearchParams(newQuery).toString();

  return window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + '?' + queryString
  );
};
